import {
    getFgPortfolio,
    type FgComponent,
    type FgPortfolioRow,
} from '@api/fg-calculator';
import {
    getMaterialStockSnapshot,
    type MaterialFacilityType,
    type MaterialStockSnapshotRow,
    type MaterialType,
} from '@api/material-stock-snapshot';
import { listMotherOrders, type MotherOrder } from '@api/mother-order';
import { UNCATEGORIZED_CATEGORY } from '@api/reportSchedules';
import {
    runMrpPlan,
    type MrpAlertLevel,
    type MrpPlanRow,
} from '@api/mrp';
import {
    getCrossVendorBalances,
    getLedgerHistory,
    type MaterialBalanceDto,
    type LedgerEventDto,
} from '@api/vendor-portal';
import { ReparoApi } from '@api';
import { type RateBasis } from '@api/inventory';
import PageHeader from '@components/common/PageHeader';
import {
    DataSheet,
    DataSheetPagination,
    dataSheetTokens,
    type DataSheetColumn,
} from '@components/ui/DataSheet';
import { DatePopover, type UiDateRange } from '@components/ui/DateRange';
import FilterDropdown from '@components/ui/Dropdown';
import { RateBasisControl } from '@components/ui/RateBasisControl';
import useToaster from '@hooks/toaster/useToaster';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { formatMaterialQuantity, formatNumber } from '@utils/materialQuantity';

import { contributingProductColumns } from './contributingProductColumns';
import ReceiptCostModal, { type CostableReceipt } from './ReceiptCostModal';
import {
    Box,
    Checkbox,
    Group,
    Modal,
    NumberInput,
    Slider,
    Text,
} from '@mantine/core';
import { Lightning } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { type FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { defaultLast4Weeks, formatDisplayRange } from '../demand/dateUtils';
import MaterialIncomingModal, {
    type MaterialIncomingDetailRow,
    type MaterialIncomingState,
} from './MaterialIncomingModal';
import { formatCount } from '@utils/currency';

// ─── Design system tokens ─────────────────────────────────────

const semanticColors = {
    positive: { primary: 'rgb(45, 187, 136)', light: 'rgba(45, 187, 136, 0.1)' },
    critical: { primary: 'rgb(205, 101, 101)', light: 'rgba(205, 101, 101, 0.1)' },
    warning: { primary: 'rgb(219, 182, 88)', light: 'rgba(219, 182, 88, 0.1)' },
    info: { primary: 'rgb(1, 149, 255)', light: 'rgba(1, 149, 255, 0.1)' },
};

const textColors = {
    primary: 'rgb(26, 32, 44)',
    secondary: 'rgb(100, 116, 139)',
    muted: 'rgb(148, 163, 184)',
};

const EVENT_TYPE_STYLES: Record<string, { color: string; bg: string }> = {
    INWARD: { color: semanticColors.positive.primary, bg: semanticColors.positive.light },
    CONSUMPTION: { color: semanticColors.critical.primary, bg: semanticColors.critical.light },
    ADJUSTMENT: { color: semanticColors.warning.primary, bg: semanticColors.warning.light },
    OPENING_BALANCE: { color: semanticColors.info.primary, bg: semanticColors.info.light },
};

const tokens = dataSheetTokens;

// ─── Helpers ───────────────────────────────────────────────────

function formatDate(dateStr: string | null): string {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()}`;
}

function EventBadge({ type }: { type: string }) {
    const s = EVENT_TYPE_STYLES[type] ?? { color: textColors.secondary, bg: 'rgba(0,0,0,0.04)' };
    return (
        <span className="inline-block rounded px-2 py-0.5" style={{ backgroundColor: s.bg, color: s.color, fontSize: 10, fontWeight: 600, letterSpacing: '0.05em' }}>
            {type.replace('_', ' ')}
        </span>
    );
}

const EVENT_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'INWARD', label: 'Inward' },
    { value: 'CONSUMPTION', label: 'Consumption' },
    { value: 'ADJUSTMENT', label: 'Adjustment' },
    { value: 'OPENING_BALANCE', label: 'Opening Balance' },
];

// ─── Balances Tab ──────────────────────────────────────────────

interface CrossVendorBalance extends MaterialBalanceDto {
    vendorId?: string;
    vendorName?: string;
}

const BalancesTab: FC = () => {
    const [vendorFilter, setVendorFilter] = useState<string>('');
    const [materialSearch, setMaterialSearch] = useState<string>('');

    const { data, isLoading } = useQuery(
        ['production-balances'],
        () => getCrossVendorBalances({}),
    );

    const balances = (data?.balances ?? []) as CrossVendorBalance[];

    const vendorOptions = useMemo(() => {
        const vendors = new Map<string, string>();
        for (const b of balances) {
            if (b.vendorId && b.vendorName) {
                vendors.set(b.vendorId, b.vendorName);
            }
        }
        return [
            { value: '', label: 'All Vendors' },
            ...[...vendors.entries()].map(([id, name]) => ({ value: id, label: name })),
        ];
    }, [balances]);

    const filteredBalances = useMemo(() => {
        let result = balances;
        if (vendorFilter) {
            result = result.filter((b) => b.vendorId === vendorFilter);
        }
        if (materialSearch) {
            const search = materialSearch.toLowerCase();
            result = result.filter((b) =>
                b.materialName.toLowerCase().includes(search),
            );
        }
        return result;
    }, [balances, vendorFilter, materialSearch]);

    // Sort: vendor first, then material
    const sortedBalances = useMemo(() =>
        [...filteredBalances].sort((a, b) => {
            const vendorCmp = (a.vendorName ?? '').localeCompare(b.vendorName ?? '');
            if (vendorCmp !== 0) return vendorCmp;
            return a.materialName.localeCompare(b.materialName);
        }),
    [filteredBalances]);

    const columns: DataSheetColumn<CrossVendorBalance>[] = useMemo(() => [
        {
            key: 'vendorName',
            header: 'Vendor',
            width: 200,
            sticky: true,
            render: (val: string | undefined) => (
                <span style={{ fontSize: 13, fontWeight: 500, color: val ? textColors.primary : textColors.muted }}>{val ?? '--'}</span>
            ),
        },
        {
            key: 'materialName',
            header: 'Material',
            width: 200,
            render: (val: string) => (
                <span style={{ fontSize: 13, color: textColors.primary }}>{val}</span>
            ),
        },
        {
            key: 'balance',
            header: 'Balance',
            width: 140,
            align: 'right' as const,
            render: (val: number, row: CrossVendorBalance) => (
                <span style={{ fontSize: 13, fontWeight: 700, color: textColors.primary }}>
                    {formatMaterialQuantity(val, row.unit)}
                </span>
            ),
        },
        {
            key: 'totalInward',
            header: 'Total Inward',
            width: 140,
            align: 'right' as const,
            render: (val: number, row: CrossVendorBalance) => (
                <span style={{ fontSize: 13, color: semanticColors.positive.primary }}>
                    {formatMaterialQuantity(val, row.unit)}
                </span>
            ),
        },
        {
            key: 'totalConsumed',
            header: 'Total Consumed',
            width: 150,
            align: 'right' as const,
            render: (val: number, row: CrossVendorBalance) => (
                <span style={{ fontSize: 13, color: semanticColors.critical.primary }}>
                    {formatMaterialQuantity(val, row.unit)}
                </span>
            ),
        },
    ], []);

    return (
        <DataSheet
            data={sortedBalances}
            columns={columns}
            isLoading={isLoading}
            getRowKey={(row, idx) => `${row.vendorId ?? 'x'}-${row.materialId}-${idx}`}
            title="Cross-Vendor Material Balances"
            subtitle={`${sortedBalances.length} entries`}
            enableSearch
            searchPlaceholder="Search materials..."
            searchValue={materialSearch}
            onSearchChange={setMaterialSearch}
            toolbarControls={
                <FilterDropdown
                    value={vendorFilter}
                    options={vendorOptions}
                    onChange={setVendorFilter}
                    placeholder="All Vendors"
                />
            }
            emptyState={
                <Text size="13px" c={textColors.muted} ta="center" p={32}>
                    No material balances found.
                </Text>
            }
        />
    );
};

// ─── History Tab ───────────────────────────────────────────────

const HistoryTab: FC = () => {
    const [vendorFilter, setVendorFilter] = useState<string>('');
    const [eventTypeFilter, setEventTypeFilter] = useState<string>('');
    const [materialSearch, setMaterialSearch] = useState<string>('');
    const [dateRange, setDateRange] = useState<UiDateRange>(defaultLast4Weeks());
    const [showCalendar, setShowCalendar] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    // Material cost is the brand's commercial data; the manufacturer never sees it, and CM
    // users hold VENDOR role, so gating on ADMIN keeps it off their screen as well as the
    // backend keeping it out of their hands.
    const { currentUser } = useCurrentUser();
    const canSeeCost = currentUser?.role === 'ADMIN';
    const [uncostedOnly, setUncostedOnly] = useState(false);
    const [costing, setCosting] = useState<CostableReceipt | null>(null);

    // Fetch vendor list from balances for the filter dropdown
    const { data: balancesData } = useQuery(
        ['production-balances'],
        () => getCrossVendorBalances({}),
    );

    const vendorOptions = useMemo(() => {
        const vendors = new Map<string, string>();
        for (const b of (balancesData?.balances ?? []) as Array<{ vendorId?: string; vendorName?: string }>) {
            if (b.vendorId && b.vendorName) {
                vendors.set(b.vendorId, b.vendorName);
            }
        }
        return [
            { value: '', label: 'All Vendors' },
            ...[...vendors.entries()].map(([id, name]) => ({ value: id, label: name })),
        ];
    }, [balancesData]);

    const queryParams = useMemo(() => ({
        vendorId: vendorFilter || undefined,
        eventType: eventTypeFilter || undefined,
        dateFrom: dateRange.startDate || undefined,
        dateTo: dateRange.endDate || undefined,
        uncostedOnly: uncostedOnly || undefined,
        page,
        pageSize,
    }), [vendorFilter, eventTypeFilter, dateRange, uncostedOnly, page, pageSize]);

    const { data, isLoading, refetch } = useQuery(
        ['production-history', queryParams],
        () => getLedgerHistory(queryParams),
    );

    type HistoryRow = LedgerEventDto & { vendorName?: string; materialName?: string };
    const events = (data?.events ?? []) as HistoryRow[];
    const total = data?.total ?? 0;

    const filteredEvents = useMemo(() => {
        if (!materialSearch) return events;
        const search = materialSearch.toLowerCase();
        return events.filter((e) =>
            (e.materialName ?? e.materialId).toLowerCase().includes(search),
        );
    }, [events, materialSearch]);

    const columns: DataSheetColumn<HistoryRow>[] = useMemo(() => [
        {
            key: 'date',
            header: 'Date',
            width: 110,
            sticky: true,
            render: (val: string) => (
                <span style={{ fontSize: 13 }}>{formatDate(val)}</span>
            ),
        },
        {
            key: 'vendorName',
            header: 'Vendor',
            width: 140,
            render: (val: string | undefined, row: HistoryRow) => (
                <span style={{ fontSize: 13, color: textColors.primary }}>{val ?? row.vendorId.slice(0, 8)}</span>
            ),
        },
        {
            key: 'materialName',
            header: 'Material',
            width: 180,
            render: (val: string | undefined, row: HistoryRow) => (
                <span style={{ fontSize: 13, fontWeight: 500, color: textColors.primary }}>{val ?? row.materialId.slice(0, 8)}</span>
            ),
        },
        {
            key: 'eventType',
            header: 'Type',
            width: 140,
            render: (val: string) => <EventBadge type={val} />,
        },
        {
            key: 'quantity',
            header: 'Quantity',
            width: 130,
            align: 'right' as const,
            render: (val: number, row: HistoryRow) => {
                const isOut = row.eventType === 'CONSUMPTION';
                return (
                    <span style={{ fontSize: 13, fontWeight: 600, color: isOut ? semanticColors.critical.primary : semanticColors.positive.primary }}>
                        {isOut ? '-' : '+'}{formatMaterialQuantity(val, row.unit)}
                    </span>
                );
            },
        },
        {
            key: 'purchaseOrderId',
            header: 'PO',
            width: 120,
            render: (val: string | null) => (
                <span style={{ fontSize: 13, color: val ? textColors.primary : textColors.muted }}>{val ?? '--'}</span>
            ),
        },
        {
            key: 'referenceId',
            header: 'Reference',
            width: 130,
            render: (val: string | null) => (
                <span style={{ fontSize: 13, color: val ? textColors.primary : textColors.muted }}>{val ?? '--'}</span>
            ),
        },
        {
            key: 'notes',
            header: 'Notes',
            width: 180,
            render: (val: string | null) => (
                <span style={{ fontSize: 13, color: val ? textColors.primary : textColors.muted }}>{val ?? '--'}</span>
            ),
        },
        // Brand-only. Absent for anyone else rather than blanked, so the manufacturer is
        // never shown a column they cannot fill.
        ...(canSeeCost
            ? [{
                key: 'unitCost',
                header: 'Cost / unit',
                width: 150,
                align: 'right' as const,
                render: (val: number | null | undefined, row: HistoryRow) => {
                    if (val !== null && val !== undefined) {
                        return (
                            <span style={{ fontSize: 13, fontWeight: 600, color: textColors.primary }}>
                                {row.currency ?? 'INR'}{' '}
                                {formatCount(val, { decimals: 2 })}
                            </span>
                        );
                    }
                    // Only a receipt can be costed; consumption has no price of its own.
                    if (row.eventType !== 'INWARD') {
                        return <span style={{ fontSize: 13, color: textColors.muted }}>--</span>;
                    }
                    return (
                        <button
                            type="button"
                            onClick={() => setCosting({
                                id: row.id,
                                materialId: row.materialId,
                                materialName: row.materialName ?? row.materialId,
                                unit: row.unit,
                                quantity: row.quantity,
                                date: row.date,
                                vendorName: row.vendorName,
                            })}
                            style={{
                                fontSize: 12,
                                fontWeight: 500,
                                color: semanticColors.positive.primary,
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            Add cost
                        </button>
                    );
                },
            } as DataSheetColumn<HistoryRow>]
            : []),
    ], [canSeeCost]);

    const totalPages = Math.ceil(total / pageSize);

    return (
        <>
        <DataSheet
            data={filteredEvents}
            columns={columns}
            isLoading={isLoading}
            getRowKey={(row) => row.id}
            title="Transaction History"
            subtitle={`${total} total events`}
            enableSearch
            searchPlaceholder="Search materials..."
            searchValue={materialSearch}
            onSearchChange={setMaterialSearch}
            toolbarControls={
                <Group gap={8} wrap="nowrap">
                    {canSeeCost && (
                        <Checkbox
                            size="xs"
                            label="Needs cost"
                            checked={uncostedOnly}
                            onChange={(e) => {
                                setUncostedOnly(e.currentTarget.checked);
                                setPage(1);
                            }}
                        />
                    )}
                    <FilterDropdown
                        value={vendorFilter}
                        options={vendorOptions}
                        onChange={(val) => {
                            setVendorFilter(val);
                            setPage(1);
                        }}
                        placeholder="All Vendors"
                    />
                    <FilterDropdown
                        value={eventTypeFilter}
                        options={EVENT_TYPE_OPTIONS}
                        onChange={(val) => {
                            setEventTypeFilter(val);
                            setPage(1);
                        }}
                        placeholder="All Types"
                    />
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setShowCalendar((prev) => !prev)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: tokens.spacing.xs,
                                height: tokens.dimensions.controlHeight,
                                padding: `0 ${tokens.spacing.sm}px`,
                                fontSize: 12,
                                fontWeight: 500,
                                color: tokens.colors.textPrimary,
                                backgroundColor: tokens.colors.surface,
                                border: `1px solid ${tokens.colors.border}`,
                                borderRadius: 6,
                                cursor: 'pointer',
                            }}
                        >
                            <span style={{ fontSize: 12 }}>📅</span>
                            <span>{formatDisplayRange(dateRange)}</span>
                        </button>
                        {showCalendar && (
                            <DatePopover
                                range={dateRange}
                                onClose={() => setShowCalendar(false)}
                                onApply={(range) => {
                                    setDateRange(range);
                                    setPage(1);
                                    setShowCalendar(false);
                                }}
                            />
                        )}
                    </div>
                </Group>
            }
            footer={
                totalPages > 1 ? (
                    <DataSheetPagination
                        currentPage={page}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        pageSizeOptions={[20, 50, 100]}
                        totalItems={total}
                        onPageChange={setPage}
                        onPageSizeChange={(size) => {
                            setPageSize(size);
                            setPage(1);
                        }}
                    />
                ) : undefined
            }
            emptyState={
                <Text size="13px" c={textColors.muted} ta="center" p={32}>
                    No transaction history found. Adjust filters or wait for vendor uploads.
                </Text>
            }
        />
        {canSeeCost && (
            <ReceiptCostModal
                receipt={costing}
                onClose={() => setCosting(null)}
                onSaved={() => void refetch()}
            />
        )}
        </>
    );
};

// ─── Material Stock Tab (RM/PM visibility, pivot view) ────────

// Keyed by string on purpose: a type the badge has not met yet (a brand's own kind of
// material) must render in neutral, not crash the page.
const MATERIAL_TYPE_STYLES: Record<string, { color: string; bg: string }> = {
    RM: { color: semanticColors.info.primary, bg: semanticColors.info.light },
    PM: { color: semanticColors.warning.primary, bg: semanticColors.warning.light },
    STAGE: { color: semanticColors.positive.primary, bg: semanticColors.positive.light },
};
const UNKNOWN_TYPE_STYLE = { color: textColors.secondary, bg: 'rgba(0,0,0,0.04)' };

const MATERIAL_TYPE_OPTIONS = [
    { value: '', label: 'All Types' },
    { value: 'RM', label: 'Raw Material' },
    { value: 'PM', label: 'Packaging Material' },
    { value: 'STAGE', label: 'Stage (intermediate)' },
];

const FACILITY_TYPE_OPTIONS = [
    { value: '', label: 'All Facilities' },
    { value: 'WAREHOUSE', label: 'Warehouses Only' },
    { value: 'CONTRACT_MANUFACTURER', label: 'Contract Manufacturers Only' },
    { value: 'VENDOR', label: 'Vendors Only' },
];

type FacilityGroup = 'WAREHOUSE' | 'CONTRACT_MANUFACTURER' | 'VENDOR';

const FACILITY_GROUP_LABEL: Record<FacilityGroup, string> = {
    WAREHOUSE: 'Warehouses',
    CONTRACT_MANUFACTURER: 'Contract Manufacturers',
    VENDOR: 'Vendors',
};

const FACILITY_GROUP_RANK: Record<FacilityGroup, number> = {
    WAREHOUSE: 0,
    CONTRACT_MANUFACTURER: 1,
    VENDOR: 2,
};

function deriveFacilityGroup(
    facilityType: MaterialFacilityType,
    vendorRole: string | null,
): FacilityGroup {
    if (facilityType === 'WAREHOUSE') return 'WAREHOUSE';
    if (vendorRole === 'Contract Manufacturer') return 'CONTRACT_MANUFACTURER';
    return 'VENDOR';
}

function TypeBadge({ type }: { type: MaterialType }) {
    const s = MATERIAL_TYPE_STYLES[type] ?? UNKNOWN_TYPE_STYLE;
    return (
        <span
            className="inline-block rounded px-2 py-0.5"
            style={{
                backgroundColor: s.bg,
                color: s.color,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.05em',
            }}
        >
            {type}
        </span>
    );
}

type MaterialPivotRow = {
    materialId: string;
    sku: string | null;
    materialName: string;
    materialType: MaterialType;
    category: string | null;
    unit: string;
    facilityQuantities: Record<string, number>;
    total: number;
    inProduction: number;
    inTransit: number;
};

// Incoming supply per material, summed from the mother-order per-line state
// vector (cancelled orders arrive zeroed from the backend). Detail rows feed
// the drill-in modal: in-production attributes to the mother order, in-transit
// to the departed shipment net of GRN receipts.
type MaterialIncoming = {
    inProduction: number;
    inTransit: number;
    inProductionRows: MaterialIncomingDetailRow[];
    inTransitRows: MaterialIncomingDetailRow[];
};

function aggregateIncomingByMaterial(
    orders: MotherOrder[],
): Map<string, MaterialIncoming> {
    const map = new Map<string, MaterialIncoming>();
    const entryFor = (materialId: string): MaterialIncoming => {
        let entry = map.get(materialId);
        if (!entry) {
            entry = {
                inProduction: 0,
                inTransit: 0,
                inProductionRows: [],
                inTransitRows: [],
            };
            map.set(materialId, entry);
        }
        return entry;
    };

    for (const order of orders) {
        for (const line of order.lines) {
            if (!line.materialId) continue;
            const entry = entryFor(line.materialId);
            entry.inTransit += line.inTransit;
            if (line.inProduction > 0) {
                entry.inProduction += line.inProduction;
                entry.inProductionRows.push({
                    ref: order.orderNumber,
                    kind: 'ORDER',
                    status: order.status,
                    expectedDate: order.expectedReadinessDate,
                    quantity: line.inProduction,
                });
            }
        }
        for (const shipment of order.shipments) {
            if (shipment.cancelledAt || shipment.manualReceivedAt) continue;
            if (!shipment.departureDate) continue;
            for (const item of shipment.items ?? []) {
                if (!item.materialId) continue;
                const qty = Math.max(
                    0,
                    item.quantity - (item.receivedQuantity ?? 0),
                );
                if (qty <= 0) continue;
                entryFor(item.materialId).inTransitRows.push({
                    ref: shipment.poNumber,
                    kind: 'SHIPMENT',
                    status: shipment.status,
                    expectedDate:
                        shipment.arrivalDate ??
                        shipment.expectedArrivalDate ??
                        null,
                    quantity: qty,
                });
            }
        }
    }
    return map;
}

// Shared with the scheduled-email filters, which persist this sentinel.
const UNCATEGORIZED_FILTER_VALUE = UNCATEGORIZED_CATEGORY;

const MaterialStockTab: FC = () => {
    const [materialTypeFilter, setMaterialTypeFilter] = useState<string>('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [facilityTypeFilter, setFacilityTypeFilter] = useState<string>('');
    const [materialSearch, setMaterialSearch] = useState<string>('');

    // materialTypeFilter is sent server-side so the backend joins materials table
    // and filters by m.material_type = 'RM' | 'PM'. Include it in the query key so
    // React Query refetches when the dropdown changes.
    const { data, isLoading } = useQuery(
        ['material-stock-snapshot', materialTypeFilter],
        () => getMaterialStockSnapshot({
            materialType: (materialTypeFilter as 'RM' | 'PM') || undefined,
        }),
    );
    const { data: motherOrders } = useQuery(['mother-orders'], () =>
        listMotherOrders(),
    );
    const [incomingModal, setIncomingModal] = useState<{
        row: MaterialPivotRow;
        state: MaterialIncomingState;
    } | null>(null);

    const rows = (data?.rows ?? []) as MaterialStockSnapshotRow[];

    const incomingByMaterial = useMemo(
        () => aggregateIncomingByMaterial(motherOrders ?? []),
        [motherOrders],
    );

    // Discover facilities and their group; order: Warehouses → CMs → Vendors,
    // alphabetical within each group.
    const facilities = useMemo(() => {
        const map = new Map<string, { group: FacilityGroup; name: string }>();
        for (const r of rows) {
            if (!map.has(r.facilityId)) {
                map.set(r.facilityId, {
                    group: deriveFacilityGroup(r.facilityType, r.vendorRole),
                    name: r.facilityName ?? r.facilityId,
                });
            }
        }
        return [...map.entries()].sort(([, a], [, b]) => {
            if (a.group !== b.group) {
                return FACILITY_GROUP_RANK[a.group] - FACILITY_GROUP_RANK[b.group];
            }
            return a.name.localeCompare(b.name);
        });
    }, [rows]);

    const visibleFacilities = useMemo(() => {
        if (!facilityTypeFilter) return facilities;
        return facilities.filter(([, info]) => info.group === facilityTypeFilter);
    }, [facilities, facilityTypeFilter]);

    // Pivot: one row per material, facility quantities in nested map
    const pivotRows = useMemo<MaterialPivotRow[]>(() => {
        const byMaterial = new Map<string, MaterialPivotRow>();
        for (const r of rows) {
            let pivot = byMaterial.get(r.materialId);
            if (!pivot) {
                pivot = {
                    materialId: r.materialId,
                    sku: r.sku,
                    materialName: r.materialName,
                    materialType: r.materialType,
                    category: r.category,
                    unit: r.unit ?? 'pcs',
                    facilityQuantities: {},
                    total: 0,
                    inProduction: 0,
                    inTransit: 0,
                };
                byMaterial.set(r.materialId, pivot);
            }
            pivot.facilityQuantities[r.facilityId] =
                (pivot.facilityQuantities[r.facilityId] ?? 0) + (r.quantity ?? 0);
        }
        return [...byMaterial.values()].sort((a, b) =>
            (a.sku ?? a.materialId).localeCompare(b.sku ?? b.materialId),
        );
    }, [rows]);

    const categoryOptions = useMemo(() => {
        const categories = new Set<string>();
        let hasUncategorized = false;
        for (const r of pivotRows) {
            if (r.category) {
                categories.add(r.category);
            } else {
                hasUncategorized = true;
            }
        }
        return [
            { value: '', label: 'All Categories' },
            ...[...categories]
                .sort((a, b) => a.localeCompare(b))
                .map((c) => ({ value: c, label: c })),
            ...(hasUncategorized
                ? [{ value: UNCATEGORIZED_FILTER_VALUE, label: 'Uncategorized' }]
                : []),
        ];
    }, [pivotRows]);

    // Total recomputed against visible facilities so filtering vendor/warehouse columns updates totals
    const filteredRows = useMemo(() => {
        // materialTypeFilter is applied server-side; category and search are filtered client-side here.
        let result = pivotRows.map((r) => ({
            ...r,
            total: visibleFacilities.reduce(
                (sum, [fid]) => sum + (r.facilityQuantities[fid] ?? 0),
                0,
            ),
            inProduction: incomingByMaterial.get(r.materialId)?.inProduction ?? 0,
            inTransit: incomingByMaterial.get(r.materialId)?.inTransit ?? 0,
        }));
        if (categoryFilter) {
            result = result.filter((r) =>
                categoryFilter === UNCATEGORIZED_FILTER_VALUE
                    ? !r.category
                    : r.category === categoryFilter,
            );
        }
        if (materialSearch) {
            const s = materialSearch.toLowerCase();
            result = result.filter(
                (r) =>
                    r.materialName.toLowerCase().includes(s) ||
                    (r.sku ?? '').toLowerCase().includes(s) ||
                    r.materialId.toLowerCase().includes(s),
            );
        }
        return result;
    }, [pivotRows, visibleFacilities, incomingByMaterial, materialTypeFilter, categoryFilter, materialSearch]);

    // Per-facility and grand totals span mixed units (g, m, pcs, …) so a
    // numeric sum across rows is meaningless. Show row count instead and
    // mark per-facility / total cells with em-dash.
    const aggregateRow = useMemo(() => {
        const agg: Record<string, string> = {
            materialName: `${filteredRows.length} materials`,
            total: '—',
            inProduction: '—',
            inTransit: '—',
        };
        for (const [facilityId] of visibleFacilities) {
            agg[`facility_${facilityId}`] = '—';
        }
        return agg;
    }, [filteredRows, visibleFacilities]);

    const columns: DataSheetColumn<MaterialPivotRow>[] = useMemo(() => {
        const cols: DataSheetColumn<MaterialPivotRow>[] = [
            {
                key: 'sku',
                header: 'SKU',
                width: 160,
                sticky: true,
                render: (val: string | null, row: MaterialPivotRow) => (
                    <span style={{ fontSize: 13, fontWeight: 500, color: textColors.primary }}>
                        {val ?? row.materialId}
                    </span>
                ),
            },
            {
                key: 'materialName',
                header: 'Material',
                width: 260,
                sticky: true,
                render: (val: string) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>{val}</span>
                ),
            },
            {
                key: 'materialType',
                header: 'Type',
                width: 70,
                render: (val: MaterialType) => <TypeBadge type={val} />,
            },
            {
                key: 'category',
                header: 'Category',
                width: 150,
                render: (val: string | null) => (
                    <span style={{ fontSize: 13, color: val ? textColors.primary : textColors.muted }}>
                        {val ?? '--'}
                    </span>
                ),
            },
        ];

        for (const [facilityId, info] of visibleFacilities) {
            cols.push({
                key: `facility_${facilityId}`,
                header: info.name,
                group: FACILITY_GROUP_LABEL[info.group],
                section: 'grouping' as const,
                width: 150,
                align: 'right' as const,
                // The quantity lives in a nested map, so the CSV exporter (which
                // reads row[col.key]) needs it spelled out.
                csvValue: (_: unknown, row: MaterialPivotRow) =>
                    row.facilityQuantities[facilityId] ?? '',
                render: (_: unknown, row: MaterialPivotRow) => {
                    const q = row.facilityQuantities[facilityId];
                    if (!q) {
                        return <span style={{ fontSize: 13, color: textColors.muted }}>--</span>;
                    }
                    return (
                        <span
                            style={{
                                fontSize: 13,
                                color: textColors.primary,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {formatMaterialQuantity(q, row.unit)}
                        </span>
                    );
                },
            });
        }

        // Incoming supply from mother orders / shipments. Clickable cells
        // (Pipeline View idiom) open the per-material breakdown modal. No
        // unit suffix — the facility / Total columns already carry it.
        const incomingCell =
            (state: MaterialIncomingState) =>
            (_: unknown, row: MaterialPivotRow) => {
                const value =
                    state === 'IN_PRODUCTION' ? row.inProduction : row.inTransit;
                if (!value) {
                    return (
                        <span style={{ fontSize: 13, color: textColors.muted }}>
                            0
                        </span>
                    );
                }
                return (
                    <span
                        onClick={() => setIncomingModal({ row, state })}
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: 'rgb(3, 105, 161)',
                            cursor: 'pointer',
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatNumber(value)}
                    </span>
                );
            };
        cols.push(
            {
                key: 'inProduction',
                header: 'In Production',
                group: 'Incoming',
                section: 'grouping' as const,
                width: 130,
                align: 'right' as const,
                render: incomingCell('IN_PRODUCTION'),
            },
            {
                key: 'inTransit',
                header: 'In Transit',
                group: 'Incoming',
                section: 'grouping' as const,
                width: 130,
                align: 'right' as const,
                render: incomingCell('IN_TRANSIT'),
            },
        );

        cols.push({
            key: 'total',
            header: 'Total',
            width: 150,
            align: 'right' as const,
            render: (val: number, row: MaterialPivotRow) => (
                <span
                    style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: textColors.primary,
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    {formatMaterialQuantity(val, row.unit)}
                </span>
            ),
        });

        return cols;
    }, [visibleFacilities]);

    // Quantities are unit-less numbers in the CSV (the screen suffixes them via
    // formatMaterialQuantity), so the export carries a Unit column instead —
    // matching the scheduled-email report.
    const downloadColumns = useMemo(() => {
        const unitColumn: DataSheetColumn<MaterialPivotRow> = {
            key: 'unit',
            header: 'Unit',
            width: 80,
        };
        const categoryIndex = columns.findIndex((c) => c.key === 'category');
        if (categoryIndex < 0) return [...columns, unitColumn];
        return [
            ...columns.slice(0, categoryIndex + 1),
            unitColumn,
            ...columns.slice(categoryIndex + 1),
        ];
    }, [columns]);

    return (
        <>
        <DataSheet
            data={filteredRows}
            columns={columns}
            allColumns={downloadColumns}
            isLoading={isLoading}
            getRowKey={(row) => row.materialId}
            title="Raw & Packaging Material Stock"
            subtitle={`${filteredRows.length} materials across ${visibleFacilities.length} facilities`}
            enableSearch
            searchPlaceholder="Search by SKU or name..."
            searchValue={materialSearch}
            onSearchChange={setMaterialSearch}
            aggregateRow={aggregateRow}
            enableDownload
            downloadFilename="rm_pm_stock.csv"
            toolbarControls={
                <Group gap={8} wrap="nowrap">
                    <FilterDropdown
                        value={materialTypeFilter}
                        options={MATERIAL_TYPE_OPTIONS}
                        onChange={setMaterialTypeFilter}
                        placeholder="All Types"
                    />
                    <FilterDropdown
                        value={categoryFilter}
                        options={categoryOptions}
                        onChange={setCategoryFilter}
                        placeholder="All Categories"
                    />
                    <FilterDropdown
                        value={facilityTypeFilter}
                        options={FACILITY_TYPE_OPTIONS}
                        onChange={setFacilityTypeFilter}
                        placeholder="All Facilities"
                    />
                </Group>
            }
            emptyState={
                <Text size="13px" c={textColors.muted} ta="center" p={32}>
                    No material stock found. Adjust filters or check facility uploads.
                </Text>
            }
        />
        {incomingModal && (
            <MaterialIncomingModal
                isOpen
                onClose={() => setIncomingModal(null)}
                sku={incomingModal.row.sku ?? incomingModal.row.materialId}
                materialName={incomingModal.row.materialName}
                unit={incomingModal.row.unit}
                state={incomingModal.state}
                rows={
                    (incomingModal.state === 'IN_PRODUCTION'
                        ? incomingByMaterial.get(incomingModal.row.materialId)
                              ?.inProductionRows
                        : incomingByMaterial.get(incomingModal.row.materialId)
                              ?.inTransitRows) ?? []
                }
            />
        )}
        </>
    );
};

// ─── Main Container ────────────────────────────────────────────

// ─── FG Calculator Tab ────────────────────────────────────────

function FgMaterialTypeBadge({ type }: { type: string }) {
    const styles = MATERIAL_TYPE_STYLES[type] ?? UNKNOWN_TYPE_STYLE;
    return (
        <span
            className="inline-block rounded px-2 py-0.5"
            style={{
                backgroundColor: styles.bg,
                color: styles.color,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.05em',
            }}
        >
            {type}
        </span>
    );
}

/**
 * "At risk" = current FG runway (daysOnHand) is shorter than the time it would
 * take to replenish (manufacturingLeadTimeDays) plus a 1.5× safety multiplier.
 * Needs both numbers known; if either is missing we can't compute risk so the
 * CTA is hidden for that row.
 */
const AT_RISK_LEAD_TIME_MULTIPLIER = 1.5;
const POST_PRODUCTION_TARGET_DAYS = 40;

const isFgAtRisk = (row: FgPortfolioRow): boolean => {
    if (row.daysOnHand === null || row.daysOnHand === undefined) return false;
    if (
        row.manufacturingLeadTimeDays === null
        || row.manufacturingLeadTimeDays === undefined
    ) return false;
    return row.daysOnHand < row.manufacturingLeadTimeDays * AT_RISK_LEAD_TIME_MULTIPLIER;
};

const computeWoPrefillForRow = (row: FgPortfolioRow): {
    quantity: number;
    dueDate: string;
} => {
    const dailyConsumption = row.consumptionPerDay ?? 0;
    const doh = row.daysOnHand ?? 0;
    const leadTime = row.manufacturingLeadTimeDays ?? 0;

    // Order-up-to formula: target = (40 + leadTime) days of cover, taking into
    // account consumption during the lead time. Floor at 0.
    const targetDays = POST_PRODUCTION_TARGET_DAYS + leadTime;
    const recommended = Math.max(0, Math.ceil((targetDays - doh) * dailyConsumption));

    const dueDate = new Date(Date.now() + leadTime * 24 * 60 * 60 * 1000)
        .toISOString()
        .slice(0, 10);

    return { quantity: recommended, dueDate };
};

const FgCalculatorTab: FC = () => {
    const navigate = useNavigate();
    const [wastagePercent, setWastagePercent] = useState<number>(0);
    const [drillInRow, setDrillInRow] = useState<FgPortfolioRow | null>(null);

    const { data, isLoading } = useQuery(
        ['fg-portfolio', wastagePercent],
        () => getFgPortfolio({ wastagePercent }),
    );

    const rows = (data ?? []) as FgPortfolioRow[];

    const handleCreateWorkOrder = (row: FgPortfolioRow) => {
        const { quantity, dueDate } = computeWoPrefillForRow(row);
        const params = new URLSearchParams({
            prefillProductId: row.productId,
            prefillQuantity: String(quantity),
            prefillDueDate: dueDate,
        });
        navigate(`/work-orders?${params.toString()}`);
    };

    const columns: DataSheetColumn<FgPortfolioRow>[] = useMemo(
        () => [
            {
                key: 'status',
                header: 'Status',
                width: 90,
                sticky: true,
                render: (_: unknown, row: FgPortfolioRow) =>
                    isFgAtRisk(row) ? <AlertBadge level="RED" /> : null,
            },
            {
                key: 'productName',
                header: 'Product',
                width: 320,
                sticky: true,
                render: (val: string, row: FgPortfolioRow) => (
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13, color: textColors.primary }}>{val}</span>
                        <span style={{ fontSize: 11, color: textColors.muted }}>
                            {row.productSku ?? row.productId}
                        </span>
                    </div>
                ),
            },
            {
                key: 'bottleneckMaterialName',
                header: 'Bottleneck Material',
                width: 280,
                render: (_: unknown, row: FgPortfolioRow) => {
                    if (!row.bottleneckMaterialName) {
                        return <span style={{ fontSize: 13, color: textColors.muted }}>--</span>;
                    }
                    return (
                        <div className="flex flex-col">
                            <span style={{ fontSize: 13, fontWeight: 500, color: textColors.primary }}>
                                {row.bottleneckMaterialName}
                            </span>
                            <span style={{ fontSize: 11, color: textColors.muted }}>
                                {row.bottleneckMaterialSku ?? row.bottleneckMaterialId ?? ''}
                            </span>
                        </div>
                    );
                },
            },
            {
                key: 'fgEquivalent',
                header: 'FG-Equivalent (units)',
                width: 160,
                align: 'right' as const,
                render: (val: number) => (
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: val > 0 ? textColors.primary : semanticColors.critical.primary,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatCount(val)}
                    </span>
                ),
            },
            {
                key: 'manufacturingLeadTimeDays',
                header: 'Lead Time',
                width: 110,
                align: 'right' as const,
                render: (val: number | null) => {
                    if (val === null || val === undefined) {
                        return (
                            <span
                                style={{ fontSize: 13, color: textColors.muted }}
                                title="Product is not mapped to a vendor with a configured lane."
                            >
                                NA
                            </span>
                        );
                    }
                    return (
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: 600,
                                color: textColors.primary,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                            title="Fastest route across all assigned vendors: production + QC + transit + delivery"
                        >
                            {formatCount(val)} days
                        </span>
                    );
                },
            },
            {
                key: 'daysOnHand',
                header: 'DoH (days)',
                width: 110,
                align: 'right' as const,
                render: (val: number | null, row: FgPortfolioRow) => {
                    if (val === null || val === undefined) {
                        return (
                            <span style={{ fontSize: 13, color: textColors.muted }} title="No consumption data">
                                --
                            </span>
                        );
                    }
                    return (
                        <div className="flex flex-col items-end">
                            <span
                                style={{
                                    fontSize: 13,
                                    fontWeight: 600,
                                    color: textColors.primary,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {val < 1 ? val.toFixed(1) : formatCount(Math.round(val))}
                            </span>
                            {row.consumptionPerDay ? (
                                <span style={{ fontSize: 10, color: textColors.muted }}>
                                    @ {row.consumptionPerDay.toFixed(1)}/day
                                </span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                key: 'reorder',
                header: '',
                width: 160,
                render: (_: unknown, row: FgPortfolioRow) => {
                    if (!isFgAtRisk(row)) return null;
                    return (
                        <Group gap={8} wrap="nowrap" justify="flex-end">
                            <Box
                                component="button"
                                onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleCreateWorkOrder(row);
                                }}
                                title={`DoH ${row.daysOnHand?.toFixed(1)} < lead time ${row.manufacturingLeadTimeDays} × ${AT_RISK_LEAD_TIME_MULTIPLIER}`}
                                style={{
                                    backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                    color: 'rgb(99, 102, 241)',
                                    fontWeight: 600,
                                    fontSize: 12,
                                    padding: '6px 12px',
                                    borderRadius: 8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    cursor: 'pointer',
                                    border: 'none',
                                    transition: '0.2s ease',
                                }}
                            >
                                <Lightning size={14} weight="fill" />
                                Create WO
                            </Box>
                        </Group>
                    );
                },
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [navigate],
    );

    /*
     * The two name columns fit the longest name in the BOM being shown. At fixed widths a
     * material code like "Seasoning_PUFF_CHEESEHERBS" ran out of its SKU cell and over the
     * Material cell beside it. 13px runs about 7.5px a character; floored at the old widths
     * so a short BOM does not shrink them, capped so one long name cannot stretch the modal
     * past the viewport.
     */
    const drillInWidths = useMemo(() => {
        const fit = (labels: Array<string | null>, min: number, max: number) => {
            const longest = Math.max(0, ...labels.map((label) => (label ?? '').length));
            return Math.min(max, Math.max(min, Math.round(longest * 7.5) + 32));
        };
        const components = drillInRow?.components ?? [];
        return {
            sku: fit(components.map((c) => c.materialSku), 160, 420),
            name: fit(components.map((c) => c.materialName), 240, 480),
        };
    }, [drillInRow]);

    const drillInColumns: DataSheetColumn<FgComponent>[] = useMemo(
        () => [
            {
                key: 'materialType',
                header: 'Type',
                width: 90,
                render: (val: 'RM' | 'PM') => <FgMaterialTypeBadge type={val} />,
            },
            {
                key: 'materialSku',
                header: 'SKU',
                width: drillInWidths.sku,
                render: (val: string | null) => (
                    <span style={{ fontSize: 13, color: val ? textColors.primary : textColors.muted }}>
                        {val ?? '--'}
                    </span>
                ),
            },
            {
                key: 'materialName',
                header: 'Material',
                width: drillInWidths.name,
                render: (val: string) => (
                    <span style={{ fontSize: 13, color: textColors.primary }}>{val}</span>
                ),
            },
            {
                key: 'qtyPerFg',
                header: 'Qty per FG',
                width: 120,
                align: 'right' as const,
                render: (val: number, row: FgComponent) => (
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMaterialQuantity(val, row.unit)}
                    </span>
                ),
            },
            {
                key: 'availableQty',
                header: 'Available',
                width: 130,
                align: 'right' as const,
                render: (val: number, row: FgComponent) => (
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMaterialQuantity(val, row.unit)}
                    </span>
                ),
            },
            {
                key: 'alternateMaxFgUnits',
                header: 'From stage stock',
                width: 200,
                align: 'right' as const,
                render: (_val: number | undefined, row: FgComponent) => {
                    const alternates = (row.alternates ?? []).filter((alt) => alt.availableQty > 0);
                    if (alternates.length === 0) {
                        return <span style={{ fontSize: 13, color: textColors.muted }}>--</span>;
                    }
                    return (
                        <div className="flex flex-col">
                            {alternates.map((alt) => (
                                <span
                                    key={alt.materialId}
                                    style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums', color: textColors.primary }}
                                >
                                    {formatMaterialQuantity(alt.availableQty, alt.unit)} {alt.materialName} = {formatCount(alt.maxFgUnits)} FG
                                </span>
                            ))}
                        </div>
                    );
                },
            },
            {
                key: 'maxFgUnits',
                header: 'Max FG',
                width: 160,
                align: 'right' as const,
                render: (val: number, row: FgComponent) => (
                    <div className="flex flex-col">
                        <span
                            style={{
                                fontSize: 13,
                                fontWeight: row.isBottleneck ? 700 : 500,
                                color: row.isBottleneck
                                    ? semanticColors.critical.primary
                                    : textColors.primary,
                                fontVariantNumeric: 'tabular-nums',
                            }}
                        >
                            {formatCount(val)}
                            {row.isBottleneck ? '  (bottleneck)' : ''}
                        </span>
                        {(row.alternateMaxFgUnits ?? 0) > 0 ? (
                            <span style={{ fontSize: 11, color: textColors.muted, fontVariantNumeric: 'tabular-nums' }}>
                                {formatCount(row.alternateMaxFgUnits ?? 0)} stage + {formatCount(row.rawMaxFgUnits ?? 0)} raw
                            </span>
                        ) : null}
                    </div>
                ),
            },
        ],
        [drillInWidths],
    );

    return (
        <div className="space-y-4">
            <div
                className="rounded border p-3"
                style={{
                    borderColor: 'rgb(254, 215, 170)',
                    backgroundColor: 'rgb(255, 247, 237)',
                }}
            >
                <Text size="13px" c="rgb(154, 64, 16)">
                    Calculated independently per SKU. Shared components may be over-allocated if produced
                    simultaneously.
                </Text>
            </div>

            <div className="flex items-center gap-6 pb-6">
                <Text size="13px" fw={500} c={textColors.primary}>
                    Wastage: {wastagePercent}%
                </Text>
                <div className="w-64">
                    <Slider
                        value={wastagePercent}
                        onChange={setWastagePercent}
                        min={0}
                        max={50}
                        step={1}
                        color="#9075ff"
                        marks={[
                            { value: 0, label: '0%' },
                            { value: 25, label: '25%' },
                            { value: 50, label: '50%' },
                        ]}
                    />
                </div>
                <Text size="11px" c={textColors.muted}>
                    Reduces each material's usable stock before computing FG capacity.
                </Text>
            </div>

            <DataSheet
                data={rows}
                columns={columns}
                isLoading={isLoading}
                getRowKey={(row) => row.productId}
                title="FG-Equivalent Portfolio"
                subtitle={`${rows.length} SKUs with a BOM`}
                enableSearch
                searchPlaceholder="Search by SKU or product..."
                onRowClick={(row) => setDrillInRow(row)}
                emptyState={
                    <Text size="13px" c="dimmed" ta="center" p={32}>
                        No SKUs with a BOM yet. Add product-material mappings under Settings &gt; Bill of
                        Materials to populate this view.
                    </Text>
                }
            />

            <Modal
                opened={drillInRow !== null}
                onClose={() => setDrillInRow(null)}
                closeButtonProps={{ 'aria-label': 'Close', color: 'gray' }}
                title={
                    drillInRow ? (
                        <div className="flex flex-col">
                            <span style={{ fontSize: 15, fontWeight: 600, color: textColors.primary }}>
                                {drillInRow.productName || drillInRow.productSku || drillInRow.productId}
                            </span>
                            <span style={{ fontSize: 12, color: textColors.muted }}>
                                {drillInRow.productSku ?? drillInRow.productId} —{' '}
                                {formatCount(drillInRow.fgEquivalent)} units possible
                                {drillInRow.manufacturingLeadTimeDays !== null &&
                                drillInRow.manufacturingLeadTimeDays !== undefined
                                    ? ` — ready in ${formatCount(drillInRow.manufacturingLeadTimeDays)} days`
                                    : ''}
                            </span>
                        </div>
                    ) : (
                        ''
                    )
                }
                // The modal grows with its name columns: the fixed ones (type, qty, available,
                // max FG) plus the two fitted ones plus its own padding.
                size={Math.max(950, 90 + drillInWidths.sku + drillInWidths.name + 120 + 130 + 100 + 48)}
                centered
            >
                {drillInRow && (
                    <DataSheet
                        data={drillInRow.components}
                        columns={drillInColumns}
                        getRowKey={(c) => c.materialId}
                        bare
                        hideHeaderBar
                    />
                )}
            </Modal>
        </div>
    );
};

// ─── MRP / Plan Tab ───────────────────────────────────────────

const ALERT_STYLES: Record<MrpAlertLevel, { color: string; bg: string; label: string }> = {
    RED: { color: semanticColors.critical.primary, bg: semanticColors.critical.light, label: 'AT RISK' },
    AMBER: { color: semanticColors.warning.primary, bg: semanticColors.warning.light, label: 'TIGHT' },
    GREEN: { color: semanticColors.info.primary, bg: semanticColors.info.light, label: 'ACTION' },
    NONE: { color: textColors.muted, bg: 'rgba(0,0,0,0.04)', label: 'COVERED' },
};

function AlertBadge({ level }: { level: MrpAlertLevel }) {
    const s = ALERT_STYLES[level];
    return (
        <span
            className="inline-block rounded px-2 py-0.5"
            style={{
                backgroundColor: s.bg,
                color: s.color,
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.05em',
            }}
        >
            {s.label}
        </span>
    );
}

function formatNumberOrDash(val: number | null | undefined, suffix = ''): string {
    if (val === null || val === undefined) return '--';
    if (Number.isNaN(val) || !Number.isFinite(val)) return '--';
    return formatCount(Math.round(val)) + suffix;
}

const MrpTab: FC = () => {
    const [daysOfCover, setDaysOfCover] = useState<number>(45);
    const [wastagePercent, setWastagePercent] = useState<number>(0);
    const [drillInRow, setDrillInRow] = useState<MrpPlanRow | null>(null);
    const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(
        () => new Set(),
    );
    const [hideAcknowledged, setHideAcknowledged] = useState<boolean>(false);

    const { currentUser } = useCurrentUser();
    const { showToastSuccess, showToastWarning } = useToaster();
    const merchantId = currentUser?.organizationId;

    // Transient "explore" override of the DoH rate policy (same pattern as the
    // Inventory tab). null = follow the saved org default echoed in the response.
    const [draftBasis, setDraftBasis] = useState<RateBasis | null>(null);
    const [draftWindow, setDraftWindow] = useState<number | null>(null);
    const [savingRate, setSavingRate] = useState(false);

    const { data, isLoading } = useQuery(
        ['mrp-plan', daysOfCover, wastagePercent, draftBasis, draftWindow],
        () =>
            runMrpPlan({
                daysOfCover,
                wastagePercent,
                basis: draftBasis ?? undefined,
                drrWindow: draftWindow ?? undefined,
            }),
    );

    const rows = data?.rows ?? [];
    const planConfig = data?.config;

    const savedBasis: RateBasis = planConfig?.basis ?? 'BACKWARD';
    const savedWindow = planConfig?.salesLookbackDays ?? 30;
    const effectiveBasis: RateBasis = draftBasis ?? savedBasis;
    const effectiveWindow = draftWindow ?? savedWindow;
    const isRateDirty =
        effectiveBasis !== savedBasis ||
        (effectiveBasis === 'BACKWARD' && effectiveWindow !== savedWindow);

    const handleResetRate = () => {
        setDraftBasis(null);
        setDraftWindow(null);
    };

    // Commit the current preview to the org default (same endpoint the Inventory
    // tab uses) — this re-bases every downstream DoH consumer, hence the toast copy.
    const handleSaveRateDefault = async () => {
        if (!merchantId) return;
        setSavingRate(true);
        try {
            await ReparoApi({
                method: 'PATCH',
                url: `/organizations/${merchantId}`,
                body: JSON.stringify({
                    lookup: effectiveBasis,
                    salesLookbackDays: effectiveWindow,
                }),
            });
            setDraftBasis(null);
            setDraftWindow(null);
            showToastSuccess({
                title: 'Saved as default',
                subtitle: 'Alerts, reports and planning will now use this basis.',
            });
        } catch {
            showToastWarning({ title: 'Could not save the default. Please try again.' });
        } finally {
            setSavingRate(false);
        }
    };

    const visibleRows = useMemo(
        () =>
            hideAcknowledged
                ? rows.filter((r) => !acknowledgedIds.has(r.materialId))
                : rows,
        [rows, hideAcknowledged, acknowledgedIds],
    );

    const alertCounts = useMemo(() => {
        const counts = { RED: 0, AMBER: 0, GREEN: 0, NONE: 0 };
        for (const r of rows) {
            counts[r.alertLevel] += 1;
        }
        return counts;
    }, [rows]);

    const toggleAcknowledged = (materialId: string) => {
        setAcknowledgedIds((prev) => {
            const next = new Set(prev);
            if (next.has(materialId)) {
                next.delete(materialId);
            } else {
                next.add(materialId);
            }
            return next;
        });
    };

    const columns: DataSheetColumn<MrpPlanRow>[] = useMemo(
        () => [
            {
                key: 'alertLevel',
                header: 'Status',
                width: 90,
                sticky: true,
                render: (val: MrpAlertLevel) => <AlertBadge level={val} />,
            },
            {
                key: 'materialName',
                header: 'Material',
                width: 240,
                sticky: true,
                render: (val: string, row: MrpPlanRow) => (
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13, fontWeight: 500, color: textColors.primary }}>
                            {val}
                        </span>
                        <span style={{ fontSize: 11, color: textColors.muted }}>
                            {row.materialSku ?? row.materialId}
                        </span>
                    </div>
                ),
            },
            {
                key: 'currentStock',
                header: 'On Hand',
                width: 170,
                align: 'right' as const,
                render: (val: number, row: MrpPlanRow) => (
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: textColors.primary }}>
                            {formatMaterialQuantity(val, row.unit)}
                        </span>
                        {(row.stageEquivalentStock ?? 0) > 0 ? (
                            <span
                                style={{ fontSize: 11, color: textColors.muted, fontVariantNumeric: 'tabular-nums' }}
                                title={(row.alternateCoverage ?? [])
                                    .map((c) => `${formatMaterialQuantity(c.availableQty, c.unit)} ${c.materialName} covers ${formatCount(c.coveredFgUnits)} FG`)
                                    .join('\n')}
                            >
                                incl. {formatMaterialQuantity(row.stageEquivalentStock ?? 0, row.unit)} as stage stock
                            </span>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'currentDoH',
                header: 'DoH (days)',
                width: 100,
                align: 'right' as const,
                render: (val: number | null) => (
                    <span
                        style={{
                            fontSize: 13,
                            fontVariantNumeric: 'tabular-nums',
                            color: val !== null ? textColors.primary : textColors.muted,
                        }}
                        title={val !== null ? undefined : 'No forecast demand for any product using this material'}
                    >
                        {formatNumberOrDash(val)}
                    </span>
                ),
            },
            {
                key: 'leadTimeDays',
                header: 'Lead Time',
                width: 100,
                align: 'right' as const,
                render: (val: number | null) => (
                    <span
                        style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: val !== null ? textColors.primary : textColors.muted }}
                        title={val !== null ? 'production + QC + transit + delivery, fastest lane' : 'No lane configured for default vendor'}
                    >
                        {val !== null ? formatNumberOrDash(val, ' d') : 'NA'}
                    </span>
                ),
            },
            {
                key: 'vendorName',
                header: 'Default Vendor',
                width: 180,
                render: (val: string | null, row: MrpPlanRow) => (
                    <div className="flex flex-col">
                        <span style={{ fontSize: 13, color: val ? textColors.primary : textColors.muted }}>
                            {val ?? 'No vendor mapped'}
                        </span>
                        {row.alternativeVendors.length > 1 ? (
                            <span style={{ fontSize: 10, color: textColors.muted }}>
                                + {row.alternativeVendors.length - 1} alt
                            </span>
                        ) : null}
                    </div>
                ),
            },
            {
                key: 'moq',
                header: 'MOQ',
                width: 110,
                align: 'right' as const,
                render: (val: number | null, row: MrpPlanRow) => (
                    <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: val !== null ? textColors.primary : textColors.muted }}>
                        {val !== null ? formatMaterialQuantity(val, row.unit) : '--'}
                    </span>
                ),
            },
            {
                key: 'recommendedPoQty',
                header: 'Reco PO',
                width: 140,
                align: 'right' as const,
                render: (val: number | null, row: MrpPlanRow) => (
                    <span
                        style={{
                            fontSize: 14,
                            fontWeight: 700,
                            color: val && val > 0 ? textColors.primary : textColors.muted,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {val !== null ? formatMaterialQuantity(val, row.unit) : '--'}
                    </span>
                ),
            },
            {
                key: 'action',
                header: '',
                width: 160,
                render: (_: unknown, row: MrpPlanRow) => {
                    const isAck = acknowledgedIds.has(row.materialId);
                    const canAct = row.recommendedPoQty !== null && row.recommendedPoQty > 0;
                    if (!canAct) {
                        return <span style={{ fontSize: 11, color: textColors.muted }}>--</span>;
                    }
                    return (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleAcknowledged(row.materialId);
                            }}
                            style={{
                                fontSize: 11,
                                fontWeight: 600,
                                padding: '4px 10px',
                                borderRadius: 4,
                                border: `1px solid ${isAck ? semanticColors.positive.primary : tokens.colors.border}`,
                                backgroundColor: isAck ? semanticColors.positive.light : 'white',
                                color: isAck ? semanticColors.positive.primary : textColors.primary,
                                cursor: 'pointer',
                            }}
                        >
                            {isAck ? '✓ Acknowledged' : 'Mark Acknowledged'}
                        </button>
                    );
                },
            },
        ],
        [acknowledgedIds],
    );

    return (
        <div className="space-y-4">
            <div
                className="rounded border p-3"
                style={{
                    borderColor: 'rgb(254, 215, 170)',
                    backgroundColor: 'rgb(255, 247, 237)',
                }}
            >
                <Text size="13px" c="rgb(154, 64, 16)">
                    {effectiveBasis === 'BACKWARD'
                        ? `Demand from past-sales DRR (${effectiveWindow}-day window).`
                        : 'Demand from forecast.'}{' '}
                    Stock = on-hand only (in-transit and open RM POs not counted in V1).
                    Default vendor = lowest cost, tie-break shortest lead time. MOQ pulled from the vendor master.
                </Text>
            </div>

            <div className="flex items-center gap-6 pb-2">
                <div className="flex items-center gap-2">
                    <Text size="13px" fw={500} c={textColors.primary}>
                        Days of Cover:
                    </Text>
                    <NumberInput
                        value={daysOfCover}
                        onChange={(val) =>
                            setDaysOfCover(typeof val === 'number' ? val : Number(val) || 45)
                        }
                        min={1}
                        max={365}
                        step={5}
                        w={100}
                        size="xs"
                    />
                </div>
                <div className="flex items-center gap-3">
                    <Text size="13px" fw={500} c={textColors.primary}>
                        Wastage: {wastagePercent}%
                    </Text>
                    <div className="w-48">
                        <Slider
                            value={wastagePercent}
                            onChange={setWastagePercent}
                            min={0}
                            max={50}
                            step={1}
                            color="#9075ff"
                            marks={[
                                { value: 0, label: '0%' },
                                { value: 25, label: '25%' },
                                { value: 50, label: '50%' },
                            ]}
                        />
                    </div>
                </div>
                <label className="flex items-center gap-2" style={{ fontSize: 12, color: textColors.secondary, cursor: 'pointer' }}>
                    <input
                        type="checkbox"
                        checked={hideAcknowledged}
                        onChange={(e) => setHideAcknowledged(e.target.checked)}
                    />
                    Hide acknowledged
                </label>
                <div className="ml-auto">
                    <RateBasisControl
                        metricLabel="DoH"
                        savedBasis={savedBasis}
                        effectiveBasis={effectiveBasis}
                        effectiveWindow={effectiveWindow}
                        isDirty={isRateDirty}
                        saving={savingRate}
                        onBasisChange={setDraftBasis}
                        onWindowChange={setDraftWindow}
                        onSave={handleSaveRateDefault}
                        onReset={handleResetRate}
                    />
                </div>
            </div>

            {alertCounts.RED > 0 || alertCounts.AMBER > 0 ? (
                <div
                    className="rounded p-3"
                    style={{
                        backgroundColor: alertCounts.RED > 0 ? semanticColors.critical.light : semanticColors.warning.light,
                        border: `1px solid ${alertCounts.RED > 0 ? semanticColors.critical.primary : semanticColors.warning.primary}`,
                    }}
                >
                    <Text size="13px" fw={600} c={alertCounts.RED > 0 ? semanticColors.critical.primary : semanticColors.warning.primary}>
                        {alertCounts.RED > 0 ? `${alertCounts.RED} material${alertCounts.RED > 1 ? 's' : ''} at risk` : null}
                        {alertCounts.RED > 0 && alertCounts.AMBER > 0 ? ' · ' : ''}
                        {alertCounts.AMBER > 0 ? `${alertCounts.AMBER} tight` : null}
                        {' — raise the PO today to avoid stockout.'}
                    </Text>
                </div>
            ) : null}

            <DataSheet
                data={visibleRows}
                columns={columns}
                isLoading={isLoading}
                getRowKey={(row) => row.materialId}
                title="Material Requirements Plan"
                subtitle={`${visibleRows.length} materials · ${alertCounts.RED} at risk · ${alertCounts.AMBER} tight · ${alertCounts.GREEN} action · ${alertCounts.NONE} covered`}
                enableSearch
                searchPlaceholder="Search materials..."
                onRowClick={(row) => setDrillInRow(row)}
                emptyState={
                    <Text size="13px" c="dimmed" ta="center" p={32}>
                        No MRP rows. Verify the merchant has a BOM, forecast data, and material stock snapshot.
                    </Text>
                }
            />

            <Modal
                opened={drillInRow !== null}
                onClose={() => setDrillInRow(null)}
                closeButtonProps={{ 'aria-label': 'Close', color: 'gray' }}
                title={
                    drillInRow ? (
                        <div className="flex flex-col">
                            <span style={{ fontSize: 15, fontWeight: 600, color: textColors.primary }}>
                                {drillInRow.materialName}
                            </span>
                            <span style={{ fontSize: 12, color: textColors.muted }}>
                                {drillInRow.materialSku ?? drillInRow.materialId} ·{' '}
                                {drillInRow.alertLevel === 'NONE'
                                    ? 'covered'
                                    : `recommend ${drillInRow.recommendedPoQty !== null ? formatMaterialQuantity(drillInRow.recommendedPoQty, drillInRow.unit) : '—'}`}
                            </span>
                        </div>
                    ) : (
                        ''
                    )
                }
                size="xl"
                centered
            >
                {drillInRow && (
                    <div className="space-y-6">
                        <div className="grid grid-cols-4 gap-4">
                            {[
                                { label: 'On Hand', value: formatMaterialQuantity(drillInRow.currentStock, drillInRow.unit) },
                                { label: 'Daily Demand', value: drillInRow.dailyMaterialDemand !== null && drillInRow.dailyMaterialDemand !== undefined ? formatMaterialQuantity(drillInRow.dailyMaterialDemand, drillInRow.unit) : '--' },
                                { label: 'Current DoH', value: formatNumberOrDash(drillInRow.currentDoH, ' d') },
                                { label: 'Lead Time', value: drillInRow.leadTimeDays !== null ? `${drillInRow.leadTimeDays} d` : 'NA' },
                            ].map((kpi) => (
                                <div
                                    key={kpi.label}
                                    style={{
                                        backgroundColor: 'rgb(248, 250, 252)',
                                        borderRadius: 8,
                                        padding: 12,
                                    }}
                                >
                                    <Text size="11px" fw={600} c={textColors.muted} className="uppercase tracking-wider">
                                        {kpi.label}
                                    </Text>
                                    <Text size="18px" fw={700} c={textColors.primary} mt={4}>
                                        {kpi.value}
                                    </Text>
                                </div>
                            ))}
                        </div>

                        {drillInRow.recommendedPoQty !== null && drillInRow.recommendedPoQty > 0 ? (
                            <div
                                style={{
                                    backgroundColor: 'rgb(248, 250, 252)',
                                    borderRadius: 8,
                                    padding: 16,
                                }}
                            >
                                <Text size="11px" fw={600} c={textColors.muted} className="uppercase tracking-wider">
                                    PO recommendation math
                                </Text>
                                <Text size="13px" c={textColors.primary} mt={6}>
                                    DoH at PO arrival = max(0, {formatNumberOrDash(drillInRow.currentDoH, ' d')} -{' '}
                                    {drillInRow.leadTimeDays} d) = {formatNumberOrDash(drillInRow.dohAtArrival, ' d')}
                                </Text>
                                <Text size="13px" c={textColors.primary}>
                                    Days to top up to {daysOfCover} d cover = {formatNumberOrDash(drillInRow.daysToTopUp, ' d')}
                                </Text>
                                <Text size="13px" c={textColors.primary}>
                                    Raw qty = {formatNumberOrDash(drillInRow.daysToTopUp, ' d')} × {formatMaterialQuantity(drillInRow.dailyMaterialDemand, drillInRow.unit)}/day = {formatMaterialQuantity(drillInRow.rawQty, drillInRow.unit)}
                                </Text>
                                <Text size="13px" c={textColors.primary} fw={600}>
                                    Recommended PO = ceil(raw / MOQ {formatMaterialQuantity(drillInRow.moq, drillInRow.unit)}) × MOQ = {formatMaterialQuantity(drillInRow.recommendedPoQty, drillInRow.unit)}
                                </Text>
                            </div>
                        ) : null}

                        <div>
                            <Text size="12px" fw={600} c={textColors.secondary} mb={6}>
                                CONTRIBUTING PRODUCTS
                            </Text>
                            <DataSheet
                                data={[...drillInRow.contributingProducts].sort(
                                    (a, b) => b.contributionToDailyDemand - a.contributionToDailyDemand,
                                )}
                                columns={contributingProductColumns(drillInRow.unit, effectiveBasis)}
                                getRowKey={(p) => p.productId}
                                bare
                                hideHeaderBar
                            />
                        </div>

                        {drillInRow.alternativeVendors.length > 0 ? (
                            <div>
                                <Text size="12px" fw={600} c={textColors.secondary} mb={6}>
                                    ALTERNATIVE VENDORS
                                </Text>
                                <DataSheet
                                    data={drillInRow.alternativeVendors}
                                    columns={[
                                        {
                                            key: 'vendorName',
                                            header: 'Vendor',
                                            width: 200,
                                            render: (val: string | null, row) => (
                                                <div className="flex items-center gap-2">
                                                    <span style={{ fontSize: 13, color: textColors.primary }}>{val ?? row.vendorId}</span>
                                                    {row.vendorId === drillInRow.vendorId ? (
                                                        <span style={{ fontSize: 9, fontWeight: 700, color: semanticColors.positive.primary, letterSpacing: '0.05em' }}>
                                                            DEFAULT
                                                        </span>
                                                    ) : null}
                                                </div>
                                            ),
                                        },
                                        {
                                            key: 'cost',
                                            header: 'Cost',
                                            width: 100,
                                            align: 'right' as const,
                                            render: (val: number | null) => (
                                                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: val !== null ? textColors.primary : textColors.muted }}>
                                                    {val !== null ? formatCount(val) : '--'}
                                                </span>
                                            ),
                                        },
                                        {
                                            key: 'leadTimeDays',
                                            header: 'Lead Time',
                                            width: 110,
                                            align: 'right' as const,
                                            render: (val: number | null) => (
                                                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: val !== null ? textColors.primary : textColors.muted }}>
                                                    {val !== null ? `${val} d` : 'NA'}
                                                </span>
                                            ),
                                        },
                                        {
                                            key: 'moq',
                                            header: 'MOQ',
                                            width: 130,
                                            align: 'right' as const,
                                            render: (val: number | null) => (
                                                <span style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums', color: val !== null ? textColors.primary : textColors.muted }}>
                                                    {val !== null ? formatMaterialQuantity(val, drillInRow.unit) : '--'}
                                                </span>
                                            ),
                                        },
                                    ]}
                                    getRowKey={(v) => v.vendorId}
                                    bare
                                    hideHeaderBar
                                />
                            </div>
                        ) : null}
                    </div>
                )}
            </Modal>
        </div>
    );
};

// ─── Main Container ────────────────────────────────────────────

const PRODUCTION_PLANNING_TABS: { id: string; label: string }[] = [
    { id: 'material-stock', label: 'RM & PM Stock' },
    { id: 'fg-calculator', label: 'FG Calculator' },
    { id: 'mrp', label: 'Plan' },
    { id: 'balances', label: 'Material Balances' },
    { id: 'history', label: 'Transaction History' },
];

const ProductionPlanningContainer: FC = () => {
    const [activeTab, setActiveTab] = useState<string>('material-stock');

    return (
        <div className="flex h-full w-full flex-col gap-6 page-shell">
            <PageHeader
                icon="🏭"
                title="Production Planning"
                subtitle="Material stock, cross-vendor balances and transaction history"
            />

            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    borderBottom: `1px solid ${tokens.colors.border}`,
                }}
            >
                {PRODUCTION_PLANNING_TABS.map((tab) => {
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                padding: `${tokens.spacing.xs}px ${tokens.spacing.md}px`,
                                height: tokens.dimensions.tabHeight,
                                fontSize: 12,
                                fontWeight: isActive ? 600 : 500,
                                color: isActive
                                    ? tokens.colors.textPrimary
                                    : tokens.colors.textSecondary,
                                backgroundColor: 'transparent',
                                border: 'none',
                                borderBottom: isActive
                                    ? `2px solid ${tokens.colors.activeTab}`
                                    : '2px solid transparent',
                                cursor: 'pointer',
                                transition: 'all 0.15s ease',
                            }}
                        >
                            {tab.label}
                        </button>
                    );
                })}
            </div>

            <Box mt={4}>
                {activeTab === 'fg-calculator' && <FgCalculatorTab />}
                {activeTab === 'material-stock' && <MaterialStockTab />}
                {activeTab === 'mrp' && <MrpTab />}
                {activeTab === 'balances' && <BalancesTab />}
                {activeTab === 'history' && <HistoryTab />}
            </Box>
        </div>
    );
};

export default ProductionPlanningContainer;
